<template>
  <div class="tl-page">
    <!-- ── Filter Bar ── -->
    <div class="tl-filters">
      <div class="tl-filters__selects">
        <select v-model="filters.tourName" class="tl-select" @change="fetchTours(1)">
          <option value="">Tour Name</option>
          <option v-for="o in tourNameOptions.slice(1)" :key="o.value" :value="o.value">{{ o.text }}</option>
        </select>
        <select v-model="filters.tourCode" class="tl-select" @change="fetchTours(1)">
          <option value="">Tour Code</option>
          <option v-for="o in tourCodeOptions.slice(1)" :key="o.value" :value="o.value">{{ o.text }}</option>
        </select>
        <select v-model="filters.cruiseLine" class="tl-select tl-select--wide" @change="fetchTours(1)">
          <option value="">Cruise Line</option>
          <option v-for="o in sortedUniqueCruiseLines.slice(1)" :key="o.value" :value="o.value">{{ o.text }}</option>
        </select>
        <select v-model="filters.year" class="tl-select tl-select--xs" @change="fetchTours(1)">
          <option value="">Year</option>
          <option v-for="o in yearOptions.slice(1)" :key="o.value" :value="o.value">{{ o.text }}</option>
        </select>
        <select v-model="filters.port" class="tl-select" @change="fetchTours(1)">
          <option value="">Port</option>
          <option v-for="o in portOptions.slice(1)" :key="o.value" :value="o.value">{{ o.text }}</option>
        </select>
      </div>
      <div class="tl-filters__actions">
        <div class="tl-search">
          <svg class="tl-search__icon" viewBox="0 0 20 20" fill="none">
            <circle cx="8.5" cy="8.5" r="5.5" stroke="#9ca3af" stroke-width="1.6" />
            <path d="M13 13l3 3" stroke="#9ca3af" stroke-width="1.6" stroke-linecap="round" />
          </svg>
          <input v-model="searchQuery" class="tl-search__input" placeholder="Search tours…"
            @input="debouncedFetchTours" />
        </div>
        <span class="tl-count">{{ pagination.totalTours }} Tours</span>
        <button class="tl-btn tl-btn--ghost" v-b-tooltip.hover title="Reset all filters" @click="resetFilters">
          ↺ Reset
        </button>
        <button class="tl-btn" :class="showForm ? 'tl-btn--danger' : 'tl-btn--primary'" @click="showForm = !showForm">
          {{ showForm ? '✕ Cancel' : '+ Add Tour' }}
        </button>
      </div>
    </div>
    <div v-if="showForm" class="tl-form-panel">
      <TourForm @close="closeForm" @tour-added="fetchTours" :tour="selectedTour" />
    </div>
    <!-- ── Loading ── -->
    <div v-if="loading" class="tl-loading">
      <div class="tl-spinner"></div>
      <span>Loading tours…</span>
    </div>

    <!-- ── Table ── -->
    <div v-else class="tl-table-card">
      <div class="tl-table-scroll">
        <table class="tl-table">
          <thead>
            <tr>
              <th class="tl-th tl-th--check"><input type="checkbox" @change="selectAll" /></th>
              <th class="tl-th tl-th--sort" @click="setSort('id')">#<span class="tl-sort-arrow">{{ sortField === 'id' ?
                (sortOrder === 'asc' ? '↑' : '↓') : '' }}</span></th>
              <th class="tl-th tl-th--sort" @click="setSort('tourName')">Tour Name<span class="tl-sort-arrow">{{
                sortField === 'tourName' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}</span></th>
              <th class="tl-th tl-th--sort" @click="setSort('tourCode')">Code<span class="tl-sort-arrow">{{
                sortField === 'tourCode' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}</span></th>
              <th class="tl-th tl-th--sort" @click="setSort('cruiseLine')">Cruise Line<span class="tl-sort-arrow">{{
                sortField === 'cruiseLine' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}</span></th>
              <th class="tl-th tl-th--sort" @click="setSort('port')">Port<span class="tl-sort-arrow">{{
                sortField === 'port' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}</span></th>
              <th class="tl-th tl-th--sort" @click="setSort('year')">Year<span class="tl-sort-arrow">{{
                sortField === 'year' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}</span></th>
              <th class="tl-th">Cities</th>
              <th class="tl-th">Duration</th>
              <th class="tl-th">Pax</th>
              <th class="tl-th">Tiers &amp; Prices</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tour in tours" :key="tour.id" class="tl-row"
              :class="{ 'tl-row--selected': selectedTours.includes(tour.id) }"
              @contextmenu.prevent="showContextMenu($event, tour)" @click="toggleSelection(tour.id)"
              @dblclick="viewTour(tour.id)">
              <td class="tl-td tl-td--check"><input type="checkbox" v-model="selectedTours" :value="tour.id" /></td>
              <td class="tl-td tl-td--id">{{ tour.id }}</td>
              <td class="tl-td tl-td--name">{{ tour.attributes.tourName }}</td>
              <td class="tl-td"><span class="tl-pill tl-pill--blue">{{ tour.attributes.tourCode }}</span></td>
              <td class="tl-td tl-td--cruise">{{ tour.attributes.cruiseLine }}</td>
              <td class="tl-td">{{ tour.attributes.port?.data?.attributes?.city || '—' }}</td>
              <td class="tl-td"><span class="tl-pill tl-pill--green">{{ tour.attributes.year }}</span></td>
              <td class="tl-td">
                <div class="tl-cities">
                  <span v-for="city in tour.attributes.cities.data" :key="city.id" class="tl-pill tl-pill--purple">{{
                    city.attributes.city }}</span>
                </div>
              </td>
              <td class="tl-td">{{ tour.attributes.duration || '—' }}</td>
              <td class="tl-td tl-td--pax">
                <span v-if="tour.attributes.paxMin || tour.attributes.paxMax">{{ tour.attributes.paxMin }}–{{
                  tour.attributes.paxMax }}</span>
                <span v-else class="tl-muted">—</span>
              </td>
              <td class="tl-td tl-td--tiers">
                <template v-if="tour.attributes.tiers && tour.attributes.tiers.length">
                  <table class="tl-tier-table">
                    <thead>
                      <tr>
                        <th>Tier</th>
                        <th>Adult</th>
                        <th>Child</th>
                        <th>E.Seats</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="tier in tour.attributes.tiers" :key="tier.value">
                        <td>{{ tier.value }}</td>
                        <td>{{ formatPrice(tier.userInputSellingPriceAdult,
                          tour.attributes.cities.data?.[0]?.attributes?.city || 'N/A') }}</td>
                        <td>{{ formatPrice(tier.userInputSellingPriceChild,
                          tour.attributes.cities.data?.[0]?.attributes?.city || 'N/A') }}</td>
                        <td>{{ formatPrice(tier.userInputSellingPriceEmptySeat,
                          tour.attributes.cities.data?.[0]?.attributes?.city || 'N/A') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </template>
                <span v-else class="tl-muted">No tiers</span>
              </td>
            </tr>
            <tr v-if="!tours.length">
              <td colspan="11" class="tl-empty">No tours found for the current filters.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <b-pagination v-if="!loading && pagination.totalPages > 1" v-model="pagination.currentPage"
      :total-rows="pagination.totalTours" :per-page="pagination.perPage" align="center" class="mt-3"
      @change="fetchTours"></b-pagination>

    <!-- ── Context Menu ── -->
    <div v-if="showMenu" :style="{ top: `${menuY}px`, left: `${menuX}px` }" class="tl-ctx" ref="contextMenu">
      <button class="tl-ctx__item" @click="viewTour(selectedTour.id)">👁 View</button>
      <button class="tl-ctx__item" @click="editSelectedTours">✏️ Edit</button>
      <button class="tl-ctx__item" @click="cloneSelectedTours">📋 Clone</button>
      <button class="tl-ctx__item tl-ctx__item--danger" @click="removeSelectedTours">🗑 Delete</button>
      <button class="tl-ctx__item" @click="exportToExcel">📊 Export</button>
    </div>
    <b-modal v-model="showCloneModal" title="Clone Tour" @ok="processClone" centered header-class="tl-modal-header"
      body-class="tl-modal-body" footer-class="tl-modal-footer">
      <b-form @submit.stop.prevent="processClone">
        <div class="tl-form-group">
          <label class="tl-label">Cruise Line:</label>
          <select v-model="newCruiseLine" class="tl-select" required>
            <option v-for="option in cruiseLineOptions" :key="option" :value="option">{{ option }}</option>
          </select>
        </div>
        <div class="tl-form-group mt-3">
          <label class="tl-label">Enter the year for the cloned tour:</label>
          <input v-model="newYear" type="number" class="tl-input" required />
        </div>
        <div class="tl-form-group mt-3">
          <label class="tl-label">Tour Name:</label>
          <input v-model="newTourName" class="tl-input" required />
        </div>
        <div class="tl-form-group mt-3">
          <label class="tl-label">Tour Code:</label>
          <input v-model="newTourCode" class="tl-input" required />
        </div>
      </b-form>
      <template #modal-footer="{ ok, cancel }">
        <button class="tl-btn tl-btn--ghost" @click="cancel()">Cancel</button>
        <button class="tl-btn tl-btn--primary" @click="ok()">OK</button>
      </template>
    </b-modal>
    <b-modal v-model="showConfirmModal" title="Confirm Creation" centered header-class="tl-modal-header"
      body-class="tl-modal-body" footer-class="tl-modal-footer">
      <div v-if="matchedItems.length > 0" class="tl-box tl-box--success">
        <h4 class="tl-box-title">Matched Items</h4>
        <ul class="tl-list">
          <li v-for="item in matchedItems" :key="item.id">{{ item.attributes.item }}</li>
        </ul>
      </div>
      <div v-if="missingItems.length > 0" class="tl-box tl-box--danger">
        <h4 class="tl-box-title">Missing Items</h4>
        <ul class="tl-list tl-list--danger">
          <li v-for="item in missingItems" :key="item.id">{{ item.name }}</li>
        </ul>
      </div>
      <div class="mt-3">
        <p v-if="missingItems.length === 0 && matchedItems.length > 0" class="tl-modal-text">All items matched for the
          selected year. Do you want to create the tour?</p>
        <p v-if="missingItems.length > 0 && matchedItems.length > 0" class="tl-modal-text">Do you still want to create
          the
          tour with missing items?</p>
        <p v-if="missingItems.length === 0 && matchedItems.length === 0" class="tl-modal-text">No items are in this
          tour. Do
          you want to create the tour without items?</p>
      </div>
      <template #modal-footer>
        <button class="tl-btn tl-btn--ghost" @click="showConfirmModal = false">Cancel</button>
        <button class="tl-btn tl-btn--primary" @click="createClonedTour">Create Tour</button>
        <router-link :to="{ path: '/calculation-london/itemlist' }">
          <button class="tl-btn tl-btn--dark">Create New Item</button>
        </router-link>
      </template>
    </b-modal>
    <b-modal v-model="showConfirmDeleteModal" title="Confirm Deletion" centered header-class="tl-modal-header"
      body-class="tl-modal-body" footer-class="tl-modal-footer">
      <p class="tl-modal-text">Are you sure you want to delete the selected tours? This action cannot be undone.</p>
      <template #modal-footer>
        <button class="tl-btn tl-btn--ghost" @click="showConfirmDeleteModal = false">Cancel</button>
        <button class="tl-btn tl-btn--danger" @click="confirmDeleteTours">Delete</button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import TourForm from './TourFormLondon.vue';
import ServiceForm from './ServiceFormLondon.vue';
import { formatPrice } from '../Helper/PriceDisplay.js';
import { generateExcel } from '../Helper/GenerateTourexcel.js';
import _ from 'lodash';

export default {
  components: {
    TourForm,
    ServiceForm
  },
  data() {
    return {
      tours: [],
      loading: false,
      showForm: false,
      selectedTour: null,
      showCloneModal: false,
      showConfirmModal: false,
      showConfirmDeleteModal: false,
      newCruiseLine: '',
      newYear: '',
      newTourName: '',
      newTourCode: '',
      matchedItems: [],
      missingItems: [],
      clonedTour: null,
      selectedTours: [],
      showMenu: false,
      menuX: 0,
      menuY: 0,
      filters: {
        tourName: '',
        tourCode: '',
        cruiseLine: '',
        year: '',
        port: ''
      },
      searchQuery: '',
      sortField: 'id',
      sortOrder: 'asc',
      pagination: {
        currentPage: 1,
        perPage: 25,
        totalTours: 0,
        totalPages: 0
      },
      optionsCache: {
        tourNames: [],
        tourCodes: [],
        years: [],
        ports: [],
        cruiseLines: []
      }
    };
  },
  computed: {
    tourNameOptions() {
      return [{ value: '', text: 'Tour Name' }, ...this.optionsCache.tourNames.map(name => ({ value: name, text: name }))];
    },
    tourCodeOptions() {
      return [{ value: '', text: 'Tour Code' }, ...this.optionsCache.tourCodes.map(code => ({ value: code, text: code }))];
    },
    yearOptions() {
      return [{ value: '', text: 'Year' }, ...this.optionsCache.years.map(year => ({ value: year, text: year }))];
    },
    portOptions() {
      return [{ value: '', text: 'Port' }, ...this.optionsCache.ports.map(port => ({ value: port, text: port }))];
    },
    sortedUniqueCruiseLines() {
      return [{ value: '', text: 'Cruise Line' }, ...this.optionsCache.cruiseLines.map(line => ({ value: line, text: line }))];
    },
    cruiseLineOptions() {
      return [
        'Norwegian Cruise Line', 'Princess Cruises', 'Azamara Cruises', 'Celebrity Cruises',
        'Compagnie Francaise de Croisieres', 'Crystal Cruises', 'Cunard', 'Disney Cruise Line',
        'Fred Olsen Cruise Lines', 'Holland America Line', 'Lindblad Expeditions', 'MSC Cruises',
        'Mystic Cruises', 'P&O Cruises', 'Regent Seven Seas Cruises', 'Virgin Voyages',
        'Windstar Cruises', 'Yachts of Seabourn', 'Oceania Cruises', 'Royal Caribbean International', 'Vidanta World Elegant',
        'Viking Ocean Cruises', 'Compagnie du Ponant', 'Atlas Ocean Voyages', 'Ambassador Cruise Line', 'Plantours', 'The Ritz-Carlton Yacht Collection'
      ].sort();
    }
  },
  created() {
    this.loadFilters();
    this.fetchOptions();
    this.fetchTours(); // Call fetchTours after loading filters to apply them
    document.addEventListener('click', this.handleClickOutside);
    this.debouncedFetchTours = _.debounce(() => this.fetchTours(1), 300);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    formatPrice,
    async fetchTours(page = this.pagination.currentPage) {
      this.loading = true;
      try {
        const start = (page - 1) * this.pagination.perPage;
        const queryParams = new URLSearchParams();

        // Populate fields
        queryParams.append('populate[port][fields][0]', 'city');
        queryParams.append('populate[cities][fields][0]', 'city');
        queryParams.append('populate[item][fields][0]', 'item');

        const fields = [
          'tourName', 'tourCode', 'cruiseLine', 'year',
          'duration', 'paxMin', 'paxMax', 'tiers',
          'timing', 'description', 'margin', 'compticket',
          'factor', 'marginChild', 'marginEmpty', 'itemFactor',
          'remarks', 'comments'
        ];

        fields.forEach((field, index) => {
          queryParams.append(`fields[${index}]`, field);
        });

        // Pagination
        queryParams.append('pagination[start]', start);
        queryParams.append('pagination[limit]', this.pagination.perPage);

        // Filters
        if (this.filters.tourName) queryParams.append('filters[tourName][$eq]', this.filters.tourName);
        if (this.filters.tourCode) queryParams.append('filters[tourCode][$eq]', this.filters.tourCode);
        if (this.filters.cruiseLine) queryParams.append('filters[cruiseLine][$eq]', this.filters.cruiseLine);
        if (this.filters.year) queryParams.append('filters[year][$eq]', this.filters.year);
        if (this.filters.port) queryParams.append('filters[port][city][$eq]', this.filters.port);

        // Search query
        if (this.searchQuery.trim()) {
          const q = this.searchQuery.trim();
          queryParams.append('filters[$or][0][tourName][$containsi]', q);
          queryParams.append('filters[$or][1][tourCode][$containsi]', q);
          queryParams.append('filters[$or][2][cruiseLine][$containsi]', q);
          queryParams.append('filters[$or][3][port][city][$containsi]', q);

          if (!isNaN(q)) {
            queryParams.append('filters[$or][4][year][$eq]', q);
          }
        }

        // Sorting (handle port special case)
        const sortField = this.sortField === 'port' ? 'port.city' : this.sortField;
        queryParams.append('sort', `${sortField}:${this.sortOrder}`);

        // Final URL
        const url = `/api/tourlondons?${queryParams.toString()}`;

        // API call
        const response = await this.$xhr.get(url);

        // Map response
        this.tours = response.data.data.map(tour => ({
          id: tour.id,
          attributes: {
            ...tour.attributes,
            tiers: tour.attributes.tiers || [],
            timing: tour.attributes.timing || '',
            description: tour.attributes.description || '',
            margin: tour.attributes.margin || 0,
            compticket: tour.attributes.compticket || 0,
            factor: tour.attributes.factor || 0,
            marginChild: tour.attributes.marginChild || 0,
            marginEmpty: tour.attributes.marginEmpty || 0,
            itemFactor: tour.attributes.itemFactor || {},
            remarks: tour.attributes.remarks || '',
            comments: tour.attributes.comments || '',
            item: tour.attributes.item || { data: [] },
            cities: tour.attributes.cities || { data: [] },
            port: tour.attributes.port || null
          }
        }));

        // Update pagination
        this.pagination = {
          currentPage: page,
          perPage: this.pagination.perPage,
          totalTours: response.data.meta.pagination.total,
          totalPages: Math.ceil(response.data.meta.pagination.total / this.pagination.perPage)
        };
      } catch (error) {
        console.error('Error fetching tours:', error);
        this.$bvToast.toast('Failed to load tours. Please try again.', { variant: 'danger', solid: true });
        this.tours = [];
        this.pagination.totalTours = 0;
        this.pagination.totalPages = 0;
      } finally {
        this.loading = false;
      }
    },
    async processClone() {
      try {
        // Check if ALL four fields are exactly the same as original
        const isSameCruiseLine = this.newCruiseLine === this.selectedTour.attributes.cruiseLine;
        const isSameTourName = this.newTourName === this.selectedTour.attributes.tourName;
        const isSameTourCode = this.newTourCode === this.selectedTour.attributes.tourCode;
        const isSameYear = parseInt(this.newYear) === parseInt(this.selectedTour.attributes.year);

        // Only restrict if ALL four fields are the same
        if (isSameCruiseLine && isSameTourName && isSameTourCode && isSameYear) {
          this.$bvToast.toast('Cannot create an exact duplicate tour. Please change at least one field (Cruise Line, Tour Name, Tour Code, or Year).', {
            title: 'Clone Aborted',
            variant: 'danger',
            solid: true
          });
          this.showCloneModal = false;
          return;
        }

        // Check if tour with same combination already exists
        const existingTourResponse = await this.$xhr.get(
          `/api/tourlondons?` +
          `filters[tourName][$eq]=${encodeURIComponent(this.newTourName)}` +
          `&filters[tourCode][$eq]=${encodeURIComponent(this.newTourCode)}` +
          `&filters[cruiseLine][$eq]=${encodeURIComponent(this.newCruiseLine)}` +
          `&filters[year][$eq]=${this.newYear}`
        );

        if (existingTourResponse.data.data.length > 0) {
          this.$bvToast.toast(`A tour with the same name, code, cruise line, and year already exists.`, {
            title: 'Clone Aborted',
            variant: 'danger',
            solid: true
          });
          this.showCloneModal = false;
          return;
        }

        let newItemFactor = {};

        if (isSameYear) {
          // If same year, we can just copy items and factors directly
          this.matchedItems = this.selectedTour.attributes.item?.data || [];
          this.missingItems = [];

          // Copy original itemFactor directly
          newItemFactor = { ...(this.selectedTour.attributes.itemFactor || {}) };
        } else {
          // If different year, we need to match items by name
          let newYearItems = [];
          let pageNum = 1;
          const pageSize = 500;
          let hasMore = true;

          while (hasMore) {
            const response = await this.$xhr.get(`/api/servicelondons?filters[year][$eq]=${this.newYear}&pagination[page]=${pageNum}&pagination[pageSize]=${pageSize}`);
            const data = response.data.data;
            const meta = response.data.meta;
            newYearItems = newYearItems.concat(data);

            if (newYearItems.length >= meta.pagination.total || data.length < pageSize) {
              hasMore = false;
            } else {
              pageNum++;
            }
          }

          // Get original items from selected tour
          const originalItems = this.selectedTour.attributes.item?.data || [];

          this.matchedItems = [];
          this.missingItems = [];

          // Create a mapping of item names to new year item IDs
          const itemNameToNewIdMap = {};
          newYearItems.forEach(item => {
            itemNameToNewIdMap[item.attributes.item.trim()] = item.id;
          });

          // Create a mapping of old item IDs to item names for lookup
          const oldIdToNameMap = {};
          originalItems.forEach(item => {
            oldIdToNameMap[item.id] = item.attributes.item.trim();
          });

          originalItems.forEach(originalItem => {
            const matchedItem = newYearItems.find(newItem =>
              newItem.attributes.item.trim() === originalItem.attributes.item.trim()
            );

            if (matchedItem) {
              this.matchedItems.push(matchedItem);
            } else {
              this.missingItems.push({
                id: originalItem.id,
                name: originalItem.attributes.item
              });
            }
          });

          // Create mapped itemFactor for the new year
          const originalItemFactor = this.selectedTour.attributes.itemFactor || {};

          // Map old item IDs to new item IDs in itemFactor
          Object.keys(originalItemFactor).forEach(oldItemId => {
            const itemName = oldIdToNameMap[oldItemId];
            if (itemName) {
              const newItemId = itemNameToNewIdMap[itemName];
              if (newItemId) {
                // Map the factor from old item ID to new item ID
                newItemFactor[newItemId] = originalItemFactor[oldItemId];
              } else {
                console.warn(`Item ${itemName} (ID: ${oldItemId}) not found in new year ${this.newYear}`);
              }
            } else {
              console.warn(`Could not find item name for ID: ${oldItemId}`);
            }
          });
        }

        const clonedTiers = this.selectedTour.attributes.tiers
          ? this.selectedTour.attributes.tiers.map(tier => {
            // Create a new tier object WITHOUT userInput fields
            const newTier = {
              value: tier.value
              // DO NOT include: userInputSellingPriceAdult
              // DO NOT include: userInputSellingPriceChild
              // DO NOT include: userInputSellingPriceEmptySeat
            };

            // You can include other non-userInput fields if they exist
            if (tier.hasOwnProperty('costPriceAdult')) {
              newTier.costPriceAdult = tier.costPriceAdult;
            }
            if (tier.hasOwnProperty('costPriceChild')) {
              newTier.costPriceChild = tier.costPriceChild;
            }
            if (tier.hasOwnProperty('costPriceEmptySeat')) {
              newTier.costPriceEmptySeat = tier.costPriceEmptySeat;
            }

            return newTier; // ADD THIS RETURN STATEMENT
          })
          : [];

        // Create a clean attributes object
        this.clonedTour = {
          attributes: {
            // Basic tour information
            tourName: this.newTourName,
            tourCode: this.newTourCode,
            cruiseLine: this.newCruiseLine,
            year: parseInt(this.newYear),

            // Copy ALL calculation fields from selected tour
            margin: this.selectedTour.attributes.margin || 0,
            compticket: this.selectedTour.attributes.compticket || 0,
            factor: this.selectedTour.attributes.factor || 0,
            marginChild: this.selectedTour.attributes.marginChild || 0,
            marginEmpty: this.selectedTour.attributes.marginEmpty || 0,

            // Use the mapped itemFactor
            itemFactor: newItemFactor,

            // Tour details
            timing: this.selectedTour.attributes.timing || '',
            description: this.selectedTour.attributes.description || '',
            duration: this.selectedTour.attributes.duration || null,
            paxMin: this.selectedTour.attributes.paxMin || null,
            paxMax: this.selectedTour.attributes.paxMax || null,

            // Tiers WITHOUT userInput fields
            tiers: clonedTiers,

            // Relationships - use matched items for new year
            item: this.matchedItems.map(item => item.id),
            cities: this.selectedTour.attributes.cities?.data
              ? this.selectedTour.attributes.cities.data.map(city => city.id)
              : [],
            port: this.selectedTour.attributes.port?.data ? this.selectedTour.attributes.port.data.id : null,

            // Remarks - preserve original remarks and add cloning info
            remarks: this.buildRemarksWithMissingItems(),

            // Comments
            comments: this.selectedTour.attributes.comments || ''
          }
        };

        console.log('Cloned tour attributes:', JSON.stringify(this.clonedTour.attributes, null, 2));
        this.showCloneModal = false;
        this.showConfirmModal = true;
      } catch (error) {
        console.error('Error processing clone:', error);
        this.$bvToast.toast('Failed to process clone', { variant: 'danger', solid: true });
      }
    },
    buildRemarksWithMissingItems() {
      const originalRemarks = this.selectedTour.attributes.remarks || '';
      const cloningInfo = `\n\n--- Cloned from tour ID: ${this.selectedTour.id} (${this.selectedTour.attributes.tourName} - ${this.selectedTour.attributes.year}) on ${new Date().toLocaleDateString()} ---`;

      let missingItemsText = '';

      if (this.missingItems.length > 0) {
        missingItemsText = `\n\nMissing items for year ${this.newYear}:\n${this.missingItems.map((item, index) => `${index + 1}) ${item.name}`).join('\n')}`;
      }

      // Add note about userInput fields not being cloned
      const userInputNote = '\n\nNote: User-input selling prices in tiers were not cloned.';

      return originalRemarks + cloningInfo + missingItemsText + userInputNote;
    },

    async createClonedTour() {
      try {
        // Final validation: Check if ALL four fields are the same
        const isSameCruiseLine = this.newCruiseLine === this.selectedTour.attributes.cruiseLine;
        const isSameTourName = this.newTourName === this.selectedTour.attributes.tourName;
        const isSameTourCode = this.newTourCode === this.selectedTour.attributes.tourCode;
        const isSameYear = parseInt(this.newYear) === this.selectedTour.attributes.year;

        if (isSameCruiseLine && isSameTourName && isSameTourCode && isSameYear) {
          this.$bvToast.toast('Cannot create an exact duplicate tour.', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
          return;
        }

        // Debug log to see what's being sent
        console.log('Creating cloned tour with data:', JSON.stringify(this.clonedTour.attributes, null, 2));

        // Ensure all numeric fields are properly typed
        const clonedAttributes = {
          ...this.clonedTour.attributes,
          margin: parseFloat(this.clonedTour.attributes.margin) || 0,
          compticket: parseFloat(this.clonedTour.attributes.compticket) || 0,
          factor: parseInt(this.clonedTour.attributes.factor) || 0,
          marginChild: parseFloat(this.clonedTour.attributes.marginChild) || 0,
          marginEmpty: parseFloat(this.clonedTour.attributes.marginEmpty) || 0,
          year: parseInt(this.clonedTour.attributes.year),
          itemFactor: typeof this.clonedTour.attributes.itemFactor === 'string'
            ? JSON.parse(this.clonedTour.attributes.itemFactor)
            : this.clonedTour.attributes.itemFactor,
          // Ensure tiers is an array
          tiers: Array.isArray(this.clonedTour.attributes.tiers)
            ? this.clonedTour.attributes.tiers
            : []
        };

        const response = await this.$xhr.post('/api/tourlondons', {
          data: clonedAttributes
        });

        console.log('Clone response:', response.data);

        this.$bvToast.toast('Tour cloned successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true
        });

        await this.fetchTours();
        await this.fetchOptions();
        this.showConfirmModal = false;

        // Reset form fields
        this.newTourName = '';
        this.newTourCode = '';
        this.newYear = '';
        this.newCruiseLine = '';
        this.clonedTour = null;
        this.matchedItems = [];
        this.missingItems = [];

      } catch (error) {
        console.error('Error cloning tour:', error);
        console.error('Error details:', error.response?.data);

        let errorMessage = 'Failed to clone tour';
        if (error.response?.data?.error?.message) {
          errorMessage += `: ${error.response.data.error.message}`;
        } else if (error.message) {
          errorMessage += `: ${error.message}`;
        }

        this.$bvToast.toast(errorMessage, {
          variant: 'danger',
          solid: true
        });
      }
    },
    cloneSelectedTours() {
      if (!this.selectedTour) {
        this.$bvToast.toast('Please select a tour to clone.', { variant: 'warning', solid: true });
        return;
      }

      // Debug: Show what's in the selected tour
      console.log('Selected tour for cloning:', JSON.stringify(this.selectedTour.attributes, null, 2));

      // Set default values for clone form - PREFILL YEAR like before
      this.newTourName = `${this.selectedTour.attributes.tourName} (Clone)`;
      this.newTourCode = this.selectedTour.attributes.tourCode;
      this.newYear = this.selectedTour.attributes.year; // Prefill year like before
      this.newCruiseLine = this.selectedTour.attributes.cruiseLine;

      // Reset clone data
      this.matchedItems = [];
      this.missingItems = [];
      this.clonedTour = null;

      this.showCloneModal = true;
      this.showMenu = false;
    },
    async fetchOptions() {
      try {
        const response = await this.$xhr.get('/api/tourlondons/filters');
        const data = response.data;

        this.optionsCache.tourNames = data.tourNames || [];
        this.optionsCache.tourCodes = data.tourCodes || [];
        this.optionsCache.years = data.years || [];
        this.optionsCache.ports = data.ports || [];
        this.optionsCache.cruiseLines = data.cruiseLines || [];

      } catch (error) {
        console.error('Error fetching filter options:', error);
        this.$bvToast.toast('Failed to load filter options.', { variant: 'danger', solid: true });
      }
    },
    showContextMenu(event, tour) {
      event.preventDefault();

      // Log the full tour data to see what's available
      console.log('Full tour data for context menu:', tour);

      // Check if item data is populated
      if (tour.attributes.item?.data) {
        console.log('Item data available:', tour.attributes.item.data.map(item => ({
          id: item.id,
          name: item.attributes.item
        })));
      }

      this.selectedTour = {
        id: tour.id,
        attributes: {
          ...tour.attributes,
          // Ensure all calculation fields are explicitly included
          margin: tour.attributes.margin || 0,
          compticket: tour.attributes.compticket || 0,
          factor: tour.attributes.factor || 0,
          marginChild: tour.attributes.marginChild || 0,
          marginEmpty: tour.attributes.marginEmpty || 0,
          itemFactor: tour.attributes.itemFactor || {},
          remarks: tour.attributes.remarks || '',
          comments: tour.attributes.comments || '',
          timing: tour.attributes.timing || '',
          description: tour.attributes.description || '',
          // Other fields
          tiers: tour.attributes.tiers || [],
          item: tour.attributes.item || { data: [] },
          cities: tour.attributes.cities || { data: [] },
          port: tour.attributes.port || null,
          duration: tour.attributes.duration || null,
          paxMin: tour.attributes.paxMin || null,
          paxMax: tour.attributes.paxMax || null
        }
      };

      console.log('Selected tour itemFactor:', this.selectedTour.attributes.itemFactor);
      console.log('Selected tour items:', this.selectedTour.attributes.item?.data?.map(item => ({
        id: item.id,
        name: item.attributes.item
      })));

      this.showMenu = true;
      this.menuX = event.clientX;
      this.menuY = event.clientY;
    },
    setSort(field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortOrder = 'asc';
      }
      this.saveFilters(); // Save sort state when sorting changes
      this.fetchTours(1);
    },
    viewTour(tourId) {
      this.$router.push({ name: 'TourDetailsLondon', params: { id: tourId } });
    },
    handleClickOutside(event) {
      const contextMenu = this.$refs.contextMenu;
      if (contextMenu && !contextMenu.contains(event.target)) {
        this.showMenu = false;
      }
    },
    editSelectedTours() {
      if (!this.selectedTour) {
        this.$bvToast.toast('Please select a tour to edit.', { variant: 'warning', solid: true });
        return;
      }
      this.showForm = true;
      this.showMenu = false;
    },
    closeForm() {
      this.showForm = false;
      this.selectedTour = null;
    },
    removeSelectedTours() {
      if (this.selectedTours.length === 0) {
        this.$bvToast.toast('Please select tours to delete.', { variant: 'warning', solid: true });
        return;
      }
      this.showConfirmDeleteModal = true;
      this.showMenu = false;
    },
    async confirmDeleteTours() {
      this.showConfirmDeleteModal = false;
      try {
        await Promise.all(this.selectedTours.map(tourId => this.$xhr.delete(`/api/tourlondons/${tourId}`)));
        await this.fetchTours();
        await this.fetchOptions();
        this.$bvToast.toast('Tours deleted successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Error deleting tours:', error);
        this.$bvToast.toast('Failed to delete some tours. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },

    selectAll(event) {
      this.selectedTours = event.target.checked ? this.tours.map(tour => tour.id) : [];
    },
    toggleSelection(tourId) {
      const index = this.selectedTours.indexOf(tourId);
      if (index >= 0) {
        this.selectedTours.splice(index, 1);
      } else {
        this.selectedTours.push(tourId);
      }
    },
    resetFilters() {
      this.filters = { tourName: '', tourCode: '', cruiseLine: '', year: '', port: '' };
      this.searchQuery = '';
      this.sortField = 'id';
      this.sortOrder = 'asc';
      localStorage.removeItem('lastTourAppliedFiltersLondon');
      this.fetchTours(1);
    },
    saveFilters() {
      localStorage.setItem('lastTourAppliedFiltersLondon', JSON.stringify({
        filters: this.filters,
        searchQuery: this.searchQuery,
        sortField: this.sortField,
        sortOrder: this.sortOrder
      }));
    },
    loadFilters() {
      const savedFilters = localStorage.getItem('lastTourAppliedFiltersLondon');
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        this.filters = parsed.filters || { tourName: '', tourCode: '', cruiseLine: '', year: '', port: '' };
        this.searchQuery = parsed.searchQuery || '';
        this.sortField = parsed.sortField || 'id';
        this.sortOrder = parsed.sortOrder || 'asc';
      }
    },
    async exportToExcel() {
      if (this.selectedTours.length === 0) {
        this.$bvToast.toast('No rows selected for export!', {
          title: 'Export Failed',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.loading = true;
      try {
        // Construct the API URL with filters, search query, and sorting
        let url = `/api/tourlondons?populate[port][fields][0]=city&populate[cities][fields][0]=city&populate[item][fields][0]=item&fields[0]=tourName&fields[1]=tourCode&fields[2]=cruiseLine&fields[3]=year&fields[4]=duration&fields[5]=paxMin&fields[6]=paxMax&fields[7]=tiers`;

        // Apply filters
        if (this.filters.tourName) url += `&filters[tourName][$eq]=${encodeURIComponent(this.filters.tourName)}`;
        if (this.filters.tourCode) url += `&filters[tourCode][$eq]=${encodeURIComponent(this.filters.tourCode)}`;
        if (this.filters.cruiseLine) url += `&filters[cruiseLine][$eq]=${encodeURIComponent(this.filters.cruiseLine)}`;
        if (this.filters.year) url += `&filters[year][$eq]=${encodeURIComponent(this.filters.year)}`;
        if (this.filters.port) url += `&filters[port][city][$eq]=${encodeURIComponent(this.filters.port)}`;

        // Apply search query
        if (this.searchQuery.trim()) {
          const encodedQuery = encodeURIComponent(this.searchQuery.trim());
          url += `&filters[$or][0][tourName][$containsi]=${encodedQuery}`;
          url += `&filters[$or][1][tourCode][$containsi]=${encodedQuery}`;
          url += `&filters[$or][2][cruiseLine][$containsi]=${encodedQuery}`;
          url += `&filters[$or][3][port][city][$containsi]=${encodedQuery}`;
          if (!isNaN(this.searchQuery.trim())) {
            url += `&filters[$or][4][year][$eq]=${encodedQuery}`;
          }
        }

        // Apply sorting
        const sortField = this.sortField === 'port' ? 'port.city' : this.sortField;
        url += `&sort=${sortField}:${this.sortOrder}`;

        // Fetch all tours (remove pagination limit)
        const response = await this.$xhr.get(url);

        // Process the response
        const allTours = response.data.data.map(tour => ({
          id: tour.id,
          attributes: {
            ...tour.attributes,
            tiers: tour.attributes.tiers || [],
            item: tour.attributes.item || { data: [] },
            cities: tour.attributes.cities || { data: [] },
            port: tour.attributes.port || null
          }
        }));

        // Filter only selected tours if necessary
        const selectedRows = this.selectedTours.length === this.tours.length
          ? allTours
          : allTours.filter(tour => this.selectedTours.includes(tour.id));

        if (selectedRows.length === 0) {
          this.$bvToast.toast('No matching rows selected for export!', {
            title: 'Export Failed',
            variant: 'warning',
            solid: true
          });
          return;
        }

        // Generate Excel file with the full dataset
        generateExcel(selectedRows);

        this.$bvToast.toast('Export successful!', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        this.$bvToast.toast('Failed to export tours. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    filters: {
      handler() {
        this.saveFilters();
      },
      deep: true
    },
    searchQuery() {
      this.saveFilters();
    }
  }
};
</script>

<style scoped>
/* ────────────── Page ────────────── */
.tl-page {
  padding: 16px 18px;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

/* ────────────── Filter Bar ────────────── */
.tl-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 8px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .05);
}

.tl-filters__selects {
  display: flex;
  flex-wrap: nowrap;
  gap: 7px;
  flex: 1;
}

.tl-filters__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

.tl-select {
  height: 32px;
  min-width: 130px;
  max-width: 160px;
  font-size: .8rem;
  padding: 0 8px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #f9fafb;
  color: #374151;
  cursor: pointer;
  outline: none;
  transition: border-color .15s;
}

.tl-select:focus {
  border-color: #E2923D;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, .15);
}

.tl-select--wide {
  min-width: 170px;
  max-width: 200px;
}

.tl-select--xs {
  min-width: 80px;
  max-width: 95px;
}

/* Search */
.tl-search {
  position: relative;
}

.tl-search__icon {
  position: absolute;
  left: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 15px;
}

.tl-search__input {
  height: 32px;
  padding: 0 10px 0 30px;
  min-width: 190px;
  font-size: .82rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #f9fafb;
  outline: none;
  transition: border-color .15s;
}

.tl-search__input:focus {
  border-color: #E2923D;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, .15);
  background: #fff;
}

.tl-count {
  background: #fff3e0;
  color: #b45309;
  font-size: .75rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  border: 1px solid #fde68a;
}

/* Buttons */
.tl-btn {
  height: 32px;
  padding: 0 14px;
  font-size: .8rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity .15s, background .15s;
  white-space: nowrap;
}

.tl-btn:hover {
  opacity: .88;
}

.tl-btn--primary {
  background: #E2923D;
  color: #fff;
}

.tl-btn--danger {
  background: #e03131;
  color: #fff;
}

.tl-btn--ghost {
  background: #f1f3f5;
  color: #374151;
  border: 1px solid #dee2e6;
}

/* Form panel */
.tl-form-panel {
  margin-bottom: 14px;
}

/* ────────────── Loading ────────────── */
.tl-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 0;
  color: #9ca3af;
  font-size: .88rem;
}

.tl-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e9ecef;
  border-top-color: #E2923D;
  border-radius: 50%;
  animation: tl-spin .7s linear infinite;
}

@keyframes tl-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ────────────── Table Card ────────────── */
.tl-table-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .06);
}

.tl-table-scroll {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 68vh;
}

/* ────────────── Table ────────────── */
.tl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .82rem;
  white-space: nowrap;
}

/* Head */
.tl-table thead tr {
  background: #E2923D;
  position: sticky;
  top: 0;
  z-index: 2;
}

.tl-th {
  padding: 10px 12px;
  text-align: center;
  color: #fff;
  font-weight: 700;
  font-size: .72rem;
  text-transform: uppercase;
  letter-spacing: .05em;
  border: none;
  white-space: nowrap;
}

.tl-th--sort {
  cursor: pointer;
  user-select: none;
}

.tl-th--sort:hover {
  background: rgba(0, 0, 0, .1);
}

.tl-th--check {
  width: 38px;
}

.tl-sort-arrow {
  margin-left: 4px;
  opacity: .85;
}

/* Body */
.tl-td {
  padding: 9px 12px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
  text-align: center;
  vertical-align: middle;
}

.tl-td--check {
  width: 38px;
}

.tl-td--id {
  width: 46px;
  font-size: .75rem;
  color: #9ca3af;
}

.tl-td--name {
  text-align: left;
  max-width: 200px;
  white-space: normal;
  font-weight: 600;
  color: #111827;
}

.tl-td--cruise {
  max-width: 180px;
  white-space: normal;
}

.tl-td--pax {
  white-space: nowrap;
}

.tl-td--tiers {
  min-width: 260px;
  white-space: normal;
}

.tl-row {
  cursor: pointer;
  transition: background .1s;
}

.tl-row:hover .tl-td {
  background: #fff8f1;
}

.tl-row--selected .tl-td {
  background: #fff3e0 !important;
}

.tl-muted {
  color: #9ca3af;
}

.tl-empty {
  text-align: center;
  padding: 40px;
  color: #9ca3af;
  font-size: .88rem;
}

/* Pills */
.tl-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: .72rem;
  font-weight: 700;
}

.tl-pill--blue {
  background: #e0e7ff;
  color: #3730a3;
}

.tl-pill--green {
  background: #dcfce7;
  color: #166534;
}

.tl-pill--purple {
  background: #f3e8ff;
  color: #6b21a8;
}

.tl-cities {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  justify-content: center;
}

/* ────────────── Tier Sub-table ────────────── */
.tl-tier-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .75rem;
}

.tl-tier-table th {
  background: #f9fafb;
  color: #6b7280;
  font-size: .65rem;
  font-weight: 700;
  text-transform: uppercase;
  padding: 3px 6px;
  border: 1px solid #e5e7eb;
  text-align: center;
}

.tl-tier-table td {
  padding: 3px 6px;
  border: 1px solid #f0f0f0;
  text-align: center;
  color: #374151;
}

.tl-tier-table tr:hover td {
  background: #f9f5ff;
}

/* ────────────── Context Menu ────────────── */
.tl-ctx {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, .13);
  padding: 5px;
  min-width: 155px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tl-ctx__item {
  display: block;
  width: 100%;
  padding: 8px 14px;
  font-size: .83rem;
  text-align: left;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #374151;
  transition: background .12s;
}

.tl-ctx__item:hover {
  background: #fff8f1;
  color: #E2923D;
}

.tl-ctx__item--danger:hover {
  background: #fff0f0;
  color: #e03131;
}

/* ────────────── Modal boxes ────────────── */
.success-box {
  background: #d1fae5;
  border: 1px solid #6ee7b7;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 8px;
}

.danger-box {
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 8px;
}

/* legacy selectors kept to avoid breaking b-pagination etc */
.custom-table {
  margin-top: 4%;
  max-height: 65vh;
  overflow-y: auto;
  display: block;
  font-size: 16px
}

.table-responsive {
  overflow-y: auto;
}

.table th,
.table td {
  width: 0.1%;
  text-align: center;
}

.table th {
  position: sticky;
  top: 0;
  z-index: 1;
}

.align-items-center {
  align-items: center;
}

.context-menu {
  position: fixed;
  background-color: #e6e0e0;
  border: 1px solid #ddd;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.2);
  width: 150px;
}

.context-menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.context-menu li {
  padding: 10px;
  cursor: pointer;
}

.context-menu li:hover {
  background-color: #edc99b;
}

.nested-table {
  width: 100%;
  border-collapse: collapse;
}

.nested-table th,
.nested-table td {
  padding: 2px;
  border: 1px solid #ddd;
}

/* ────────────── Clone Modal Styles ────────────── */
:deep(.tl-modal-header) {
  border-bottom: 1px solid #f1f5f9;
  padding: 20px 24px;
}

:deep(.tl-modal-header .modal-title) {
  font-size: 1.15rem;
  font-weight: 800;
  color: #111827;
}

:deep(.tl-modal-body) {
  padding: 24px;
}

:deep(.tl-modal-footer) {
  border-top: 1px solid #f1f5f9;
  padding: 16px 24px;
  display: flex !important;
  justify-content: flex-end;
  gap: 10px;
}

.tl-form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tl-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.05em;
  margin: 0;
}

.tl-input,
.tl-select {
  height: 40px;
  width: 100%;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: .92rem;
  outline: none;
  transition: all .2s;
  background: #f8fafc;
}

.tl-input:focus,
.tl-select:focus {
  border-color: #E2923D;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, .15);
}

.tl-modal-text {
  font-size: 0.95rem;
  color: #374151;
  font-weight: 500;
}

/* Modal Boxes */
.tl-box {
  padding: 16px;
  border-radius: 10px;
  margin-bottom: 12px;
}

.tl-box--success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.tl-box--danger {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.tl-box-title {
  font-size: 1rem;
  font-weight: 800;
  margin-bottom: 8px;
}

.tl-box--success .tl-box-title {
  color: #166534;
}

.tl-box--danger .tl-box-title {
  color: #991b1b;
}

.tl-list {
  margin: 0;
  padding-left: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #1e293b;
}

.tl-list li {
  margin-bottom: 4px;
}

.tl-list--danger {
  color: #991b1b;
}

.tl-btn--dark {
  background: #1e293b;
  color: #fff;
}

.tl-btn--dark:hover {
  background: #0f172a;
}

.success-box {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  padding: 10px;
}

.danger-box {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  padding: 10px;
}

.tour-row:hover {
  background-color: #edc99b;
}

.mb-3 {
  margin-bottom: 1rem;
  margin-top: 2%;
  margin-left: 1.4%;
}

.mr-3 {
  max-width: 14rem;
  margin-top: 20px;
}

.mr-4 {
  margin-right: 1rem;
  max-width: 20rem;
  margin-inline: 2rem;
  margin-top: 20px;
}
</style>